import { ref, computed } from 'vue'
import type { Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from '@ai/ui-components/toast'
import type { DocumentItem } from 'shared/src'
import { useKnowledgeApi } from '@/composables/api/useKnowledgeApi.ts'
import { LIST_ACTION_ICONS } from '@/components/BusinessLogic/TableActions'

export const useDocumentDownload = (
  selectedKeys: Ref<(string | number)[]>,
  allSelectedKeys?: Ref<(string | number)[]>,
) => {
  const { t } = useI18n()
  const toast = useToast()
  const { downloadDocumentsBulk, downloadAllDocuments } = useKnowledgeApi()

  const isBulkDownloading = ref(false)
  const isDownloadingAll = ref(false)

  const triggerBlobDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob)
    const link = window.document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
    URL.revokeObjectURL(url)
  }

  const downloadDocument = async (item: DocumentItem) => {
    const blob = await downloadDocumentsBulk([item.id])
    if (blob) {
      triggerBlobDownload(blob, 'documents.zip')
      toast.push({
        type: 'notice',
        description: t('n_documents_downloaded_successfully', { count: 1 }),
        autoDismiss: 4000,
      })
    }
  }

  const onBulkDownload = async () => {
    if (selectedKeys.value.length === 0) return
    isBulkDownloading.value = true
    try {
      const downloadCount = selectedKeys.value.length
      const ids = selectedKeys.value.map(String)
      const blob = await downloadDocumentsBulk(ids)
      if (blob) triggerBlobDownload(blob, 'documents.zip')

      const unavailableCount = allSelectedKeys
        ? allSelectedKeys.value.length - selectedKeys.value.length
        : 0
      const title = t('n_documents_downloaded_successfully', { count: downloadCount })
      const description =
        unavailableCount === 0
          ? undefined
          : unavailableCount === 1
            ? t('one_document_not_available')
            : t('n_documents_not_available', [unavailableCount])

      toast.push({
        type: 'notice',
        description: [title, description].filter(Boolean).join('. '),
        autoDismiss: 4000,
      })
    } finally {
      isBulkDownloading.value = false
    }
  }

  const onDownloadAll = async () => {
    isDownloadingAll.value = true
    try {
      const blob = await downloadAllDocuments()
      if (blob) triggerBlobDownload(blob, 'documents.zip')
    } finally {
      isDownloadingAll.value = false
    }
  }

  const downloadAllActions = computed(() => [
    {
      label: t('download_all'),
      value: 'download-all',
      icon: LIST_ACTION_ICONS.download,
      handler: onDownloadAll,
      disabled: isDownloadingAll.value,
      dataTestid: 'action-download-all',
    },
  ])

  return {
    isBulkDownloading,
    isDownloadingAll,
    downloadDocument,
    onBulkDownload,
    onDownloadAll,
    downloadAllActions,
  }
}
